import React, { useState } from "react";
import "../Style/Forms.css";

function ContactSection() {
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    service: "",
    budget: "",
    deadline: "",
    referral: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Only allows alphabets
  const handleNameChange = (e) => {
    const { name, value } = e.target;
    const alphabetOnly = value.replace(/[^a-zA-Z]/g, "");

    setFormData({
      ...formData,
      [name]: alphabetOnly,
    });
  };

  // Service display text
  const getServiceText = (value) => {
    const services = {
      "ui-ux": "UI / UX Design",
      web: "Web Development",
      app: "App Development",
      saas: "Saas Product Development",
      marketing: "Digital Marketing",
      graphic: "Graphic Design",
      video: "Video Editing",
    };

    return services[value] || value || "Not specified";
  };

  // Budget display text
  const getBudgetText = (value) => {
    const budgets = {
      lt5k: "Less than ₹5,000",
      "5k-10k": "₹5,000 – ₹10,000",
      "10k-25k": "₹10,000 – ₹25,000",
      "25k-50k": "₹25,000 – ₹50,000",
      gt50k: "₹50,000+",
    };

    return budgets[value] || value || "Not specified";
  };

  // Deadline display text
  const getDeadlineText = (value) => {
    const deadlines = {
      lt4w: "Less than 4 Weeks",
      "1-2m": "1–2 Months",
      "3-6m": "3–6 Months",
      gt6m: "6+ Months",
    };

    return deadlines[value] || value || "Not specified";
  };

  // Referral display text
  const getReferralText = (value) => {
    const referrals = {
      instagram: "Instagram",
      linkedin: "LinkedIn",
      twitter: "Twitter / X",
      google: "Google Search",
      referral: "Referral",
      other: "Other",
    };

    return referrals[value] || value || "Not specified";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // WhatsApp number
    const whatsappNumber = "919487812715";

    // Validation
    if (!formData.firstName) {
      alert("Please enter your first name");
      return;
    }

    if (!formData.lastName) {
      alert("Please enter your last name");
      return;
    }

    if (!formData.email) {
      alert("Please enter your email address");
      return;
    }

    if (!formData.service) {
      alert("Please select a service");
      return;
    }

    if (!formData.budget) {
      alert("Please select your budget");
      return;
    }

    if (!formData.deadline) {
      alert("Please select your project deadline");
      return;
    }

    if (!formData.referral) {
      alert("Please tell us how you heard about us");
      return;
    }

    // Combine full name
    const fullName = [
      formData.firstName,
      formData.middleName,
      formData.lastName,
    ]
      .filter(Boolean)
      .join(" ");

    // Create WhatsApp message
    let message =
      "Hello, I would like to inquire about a project.%0A%0A";

    message += "*Project Details:*%0A";
    message += `Name: ${fullName}%0A`;
    message += `Email: ${formData.email}%0A`;
    message += `Service Needed: ${getServiceText(formData.service)}%0A`;
    message += `Budget: ${getBudgetText(formData.budget)}%0A`;
    message += `Deadline: ${getDeadlineText(formData.deadline)}%0A`;
    message += `How did they hear about us: ${getReferralText(
      formData.referral
    )}%0A%0A`;

    if (formData.message) {
      message += `*Message:*%0A${formData.message}%0A%0A`;
    }

    message += "*Inquiry from website contact form*";

    // WhatsApp URL
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;

    // Open WhatsApp
    window.open(whatsappUrl, "_blank");

    // Reset form
    setFormData({
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      service: "",
      budget: "",
      deadline: "",
      referral: "",
      message: "",
    });
  };

  return (
    <section className="contact-section" id="contactsec">

      {/* ================= HERO TEXT ================= */}
      <div className="contact-hero">
        <span className="contact-eyebrow">Start a project</span>

        <h1 className="contact-title">
          Let's Build Something That Matters
        </h1>

        <p className="contact-subtitle">
          Have an idea or a project in mind?
          <br />
          Talk to our experts and turn your vision into a powerful digital
          product.
        </p>
      </div>

      {/* ================= MAIN CONTENT ================= */}
      <div className="contact-body">

        {/* ================= LEFT INTRO CARD ================= */}
        <div className="intro-card">

          <div className="intro-badge" aria-hidden="true">
            <span>M</span>
          </div>

          <div className="intro-info">

            <p className="intro-name">MPeoples Business Solutions</p>

            <p className="intro-role">
              We're here to help
            </p>

            <p className="intro-text">
              Got a project in mind?{" "}
              <span className="intro-link">
                Connect with our team
              </span>{" "}
              to discuss your requirements in detail and how we can help your
              business grow. You can also request a free custom quote if the
              scope of work is clear.
            </p>

            {/* WhatsApp Button */}
            <a
              href="https://wa.me/919487812715"
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-link"
            >
              <button
                type="button"
                className="whatsapp-btn"
              >
                <svg
                  className="wa-icon"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M23.5 8.5A10.44 10.44 0 0 0 16 5.5C10.75 5.5 6.5 9.75 6.5 15a9.45 9.45 0 0 0 1.38 4.93L6.5 26.5l6.73-1.76A9.5 9.5 0 0 0 16 25.5c5.25 0 9.5-4.25 9.5-9.5a9.45 9.45 0 0 0-2-5.5Z"
                    stroke="currentColor"
                    strokeWidth="1.4"
                  />

                  <path
                    d="M21 18.92c-.3.84-1.48 1.54-2.44 1.74-.65.13-1.5.24-4.36-1.87-2.9-2.14-3.9-5.07-3.97-5.3-.07-.23-.6-1.6.27-2.74.35-.46.72-.7 1.06-.73.27-.03.54 0 .77.01.25.01.58-.09.9.7.34.82.42 1.52.29 1.63-.12.12-.46.3-.7.56-.23.26-.44.53-.19.97.25.44 1.12 1.85 2.4 2.99 1.65 1.47 3.04 1.92 3.47 2.14.43.22.68.18.93-.1.25-.28.7-.82.88-1.1.18-.28.37-.23.62-.14.25.1 1.6.75 1.87.89.27.14.46.2.52.31.06.11.06.65-.24 1.24Z"
                    fill="currentColor"
                  />
                </svg>

                Connect with MPeoples
              </button>
            </a>

          </div>
        </div>

        {/* ================= RIGHT FORM ================= */}
        <div className="form-panel">

          <form
            className="contact-form"
            onSubmit={handleSubmit}
          >

            {/* NAME ROW */}
            <div className="form-row form-row-3">

              <div className="form-group">
                <label className="form-label">
                  First Name*
                </label>

                <input
                  type="text"
                  name="firstName"
                  className="form-input"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={handleNameChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Middle Name
                </label>

                <input
                  type="text"
                  name="middleName"
                  className="form-input"
                  placeholder="Middle name"
                  value={formData.middleName}
                  onChange={handleNameChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Last Name*
                </label>

                <input
                  type="text"
                  name="lastName"
                  className="form-input"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={handleNameChange}
                  required
                />
              </div>

            </div>

            {/* EMAIL */}
            <div className="form-group full-width">

              <label className="form-label">
                Email Address*
              </label>

              <input
                type="email"
                name="email"
                className="form-input"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                required
                pattern="^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$"
                onInvalid={(e) =>
                  e.target.setCustomValidity(
                    "Please enter a valid email address."
                  )
                }
                onInput={(e) =>
                  e.target.setCustomValidity("")
                }
              />

            </div>

            {/* SERVICE + BUDGET */}
            <div className="form-row">

              <div className="form-group">

                <label className="form-label">
                  How we can help?*
                </label>

                <select
                  name="service"
                  className="form-select"
                  value={formData.service}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled hidden>
                    Services Dropdown -Select
                  </option>

                  <option value="ui-ux">
                    UI / UX Design
                  </option>

                  <option value="web">
                    Web Development
                  </option>

                  <option value="app">
                    App Development
                  </option>

                  <option value="saas">
                    Saas Product Development
                  </option>

                  <option value="marketing">
                    Digital Marketing
                  </option>

                  <option value="graphic">
                    Graphic Design
                  </option>

                  <option value="video">
                    Video Editing
                  </option>
                </select>

              </div>

              <div className="form-group">

                <label className="form-label">
                  What's your budget?*
                </label>

                <select
                  name="budget"
                  className="form-select"
                  value={formData.budget}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled hidden>
                    Amount
                  </option>

                  <option value="lt5k">
                    Less than ₹5,000
                  </option>

                  <option value="5k-10k">
                    ₹5,000 – ₹10,000
                  </option>

                  <option value="10k-25k">
                    ₹10,000 – ₹25,000
                  </option>

                  <option value="25k-50k">
                    ₹25,000 – ₹50,000
                  </option>

                  <option value="gt50k">
                    ₹50,000+
                  </option>
                </select>

              </div>

            </div>

            {/* DEADLINE + REFERRAL */}
            <div className="form-row">

              <div className="form-group">

                <label className="form-label">
                  What's your project deadline?*
                </label>

                <select
                  name="deadline"
                  className="form-select"
                  value={formData.deadline}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled hidden>
                    Select deadline
                  </option>

                  <option value="lt4w">
                    Less than 4 Weeks
                  </option>

                  <option value="1-2m">
                    1–2 Months
                  </option>

                  <option value="3-6m">
                    3–6 Months
                  </option>

                  <option value="gt6m">
                    6+ Months
                  </option>
                </select>

              </div>

              <div className="form-group">

                <label className="form-label">
                  How did you hear about us?*
                </label>

                <select
                  name="referral"
                  className="form-select"
                  value={formData.referral}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled hidden>
                    Social media dropdown
                  </option>

                  <option value="instagram">
                    Instagram
                  </option>

                  <option value="linkedin">
                    LinkedIn
                  </option>

                  <option value="twitter">
                    Twitter / X
                  </option>

                  <option value="google">
                    Google Search
                  </option>

                  <option value="referral">
                    Referral
                  </option>

                  <option value="other">
                    Other
                  </option>
                </select>

              </div>

            </div>

            {/* MESSAGE */}
            <div className="form-group full-width">

              <label className="form-label">
                Message
              </label>

              <textarea
                name="message"
                className="form-textarea"
                placeholder="Tell us more about your project..."
                rows={4}
                value={formData.message}
                onChange={handleChange}
              />

            </div>

            {/* ACTION BUTTONS */}
            <div className="form-actions">

              <button
                type="submit"
                className="btn-primary"
              >
                Start Your Project
              </button>

              {/* Phone consultation */}
              <a
                href="tel:+919487812715"
                style={{
                  textDecoration: "none",
                  border: "none",
                }}
              >
                <button
                  type="button"
                  className="btn-consultation"
                >
                  Free consultation

                  <span className="arrow-circle">

                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7 17L17 7M17 7H7M17 7V17"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                  </span>
                </button>
              </a>

            </div>

          </form>

        </div>
      </div>
    </section>
  );
}

export default ContactSection;